/**
 * paths — the single source of truth that maps the legacy "tab id" used
 * everywhere in the codebase to the new REST-resource paths the rest of
 * the SaaS industry uses (Salesforce, HubSpot, Linear, Stripe).
 *
 * Anything that needs to know "the URL for tab X" reads it here:
 *   - Sidebar links
 *   - Command palette navigate actions
 *   - Dashboard tile click-throughs
 *   - Legacy `/vendor?tab=X&id=Y` redirect
 *
 * Why a map instead of inlining: when we add a new resource we touch one
 * file. When we rename `/leads` → `/contacts` later, same. Without this
 * indirection the tab-id strings — which appear in 60+ places — would
 * leak into URL space and renames become 60-file refactors.
 */

export type TabId =
  | 'dashboard'
  | 'aiCrm'
  | 'leads'
  | 'pipeline'
  | 'activities'
  | 'tasks'
  | 'calendar'
  | 'scheduling'
  | 'emailEngagement'
  | 'duplicates'
  | 'conversations'
  | 'aiCopilot'
  | 'forecasts'
  | 'sequences'
  | 'approvals'
  | 'insights'
  | 'integrations'
  | 'customObjects'
  | 'automations'
  | 'chat'
  | 'autoResponses'
  | 'mail'
  | 'accounts'
  // Contacts (CRM IA consolidation M2, 2026-07-03) — a view of the
  // combined "Accounts & Contacts" surface, not a standalone page.
  | 'contacts'
  | 'routing'
  | 'renewals'
  // Marketing module. `marketingHub` (Overview) is the group's landing page;
  // `campaigns` carries Templates/Lists/Suppressions as in-page tabs.
  // `marketingTemplates` / `marketingLists` / `segments` / `forms` are now
  // legacy redirect-only tabs (IA change 2026-07-24) — their routes redirect;
  // kept here so old deep links still resolve.
  | 'marketingHub'
  | 'campaigns'
  | 'marketingTemplates'
  | 'marketingLists'
  | 'segments'
  | 'forms'
  // Phase 1 — Proposal module. Sits after AI CRM in the sidebar.
  | 'proposals'
  | 'proposalsTemplates'
  | 'proposalsDrafts'
  | 'proposalsSent'
  | 'proposalsArchive'
  | 'proposalsSettings'
  // Contract module — sits after Proposals. Detail / edit /
  // deliverables / payments are sub-paths of `/contracts/:id` and
  // are registered directly in router.tsx (no tab id needed since
  // they're not sidebar entries).
  | 'contracts'
  | 'contractsAll'
  | 'contractsCreate'
  | 'contractsPendingSignatures'
  | 'projects'
  // `dashboards` removed in Phase 19 — the unified `/` dashboard absorbs
  // the multi-layout builder. Legacy /dashboards URL still redirects.
  | 'invoices'
  | 'finance'
  // `financeAll` is a synonym for `finance` (→ /finance) so the Finance
  // nav group can list a ledger child without double-highlighting the
  // group header — mirrors `contractsAll`.
  | 'financeAll'
  | 'reports'
  | 'reportsBuilder'
  | 'staff'
  | 'roles'
  | 'profiles'
  // RBAC v2 R2 — unified Permissions hub (Roles + Profiles in one place).
  | 'permissions'
  | 'settings'
  | 'settingsAdmin'
  | 'emailSenders'
  | 'profile'
  | 'security'
  | 'audit'
  | 'billing'
  // Affiliate module — the customer-facing Affiliate hub (wallet, link, payouts). Sits beside
  // `billing` in Settings; gated on billing.read like the billing page.
  | 'referrals'
  | 'faq'
  | 'developers'

/**
 * Authoritative path for each tab id. Detail paths are derived (see
 * `pathForTab` below) by appending `/:id` — only the *list* path lives
 * here.
 */
export const PATH_FOR_TAB: Record<TabId, string> = {
  // Root. The bare `/` redirects to /ai-copilot (the default home screen).
  // The CRM Cockpit was merged into the AI CRM home on 2026-07-09; the
  // standalone `/dashboard` route + sidebar item are commented out (see
  // router.tsx / VendorAdminPage.tsx). This entry is kept so the `dashboard`
  // TabId still resolves and the merge stays trivially reversible — but the
  // path is inert (a stray /dashboard falls through to the /ai-crm catch-all).
  dashboard: '/dashboard',

  // AI CRM suite
  aiCrm: '/ai-crm',
  leads: '/leads',
  pipeline: '/pipeline',
  activities: '/activities',
  // CRM IA consolidation (2026-07-03): tasks/calendar/scheduling are tabs
  // of the My Work hub; duplicates is a Leads view; email engagement is an
  // Insights band. Values point at the new canonical homes so palette /
  // legacy `?tab=` navigations land in one hop; the old URLs (/tasks, …)
  // also redirect at the route layer for bookmarks. Query-string values
  // never match `tabForPath` (it compares pathnames) — that's fine, these
  // retired tabs no longer need sidebar highlighting of their own.
  // URL-key remediation (2026-08-22): these were query strings on another
  // page (`/activities?tab=tasks`), which cost more than looks. `tabForPath`
  // compares pathnames, so a query-string destination could never highlight
  // its own sidebar entry or produce a correct breadcrumb, and analytics
  // grouped all four under one pageview. They are real paths now; the old
  // query forms redirect (see router.tsx).
  tasks: '/tasks',
  calendar: '/calendar',
  scheduling: '/scheduling',
  emailEngagement: '/insights',
  duplicates: '/leads/duplicates',
  conversations: '/conversations',
  aiCopilot: '/ai-copilot',
  forecasts: '/forecasts',
  sequences: '/sequences',
  approvals: '/approvals',
  insights: '/insights',
  // Marketing module (audit follow-up)
  marketingHub: '/marketing',
  campaigns: '/campaigns',
  marketingTemplates: '/marketing/templates',
  marketingLists: '/marketing/lists-admin',
  segments: '/segments',
  forms: '/forms',
  chat: '/chat',
  // Auto-responses is configured from inside Chat (Phase 21) —
  // nest the URL to match the access pattern. Legacy `/auto-responses`
  // still redirects via the route layer.
  autoResponses: '/chat/auto-responses',
  // Mail (ADR-0007) — per-user unified mailbox, distinct from the lead-centric
  // Chat. Lives next to Chat in the sidebar.
  mail: '/mail',
  accounts: '/accounts',
  // Contacts view of the Accounts & Contacts surface (M2). A real path since
  // 2026-08-22 — Contacts is a navigable destination, not a view mode, and a
  // `?view=` destination cannot highlight itself in the sidebar. `/accounts`
  // still hosts the shared surface; `?view=contacts` redirects here.
  contacts: '/contacts',
  // Proposal module sub-tree. `proposals` lands on Drafts by default
  // (the editor surface most reps hit first).
  proposals: '/proposals',
  proposalsTemplates: '/proposals/templates',
  proposalsDrafts: '/proposals/drafts',
  proposalsSent: '/proposals/sent',
  proposalsArchive: '/proposals/archive',
  proposalsSettings: '/proposals/settings',
  // Contract module — list + create. Detail / edit / deliverables /
  // payments use `pathForContract*()` helpers below since they're
  // parameterised by contract id.
  contracts: '/contracts',
  // `contractsAll` is a synonym for `contracts` — exists so the sidebar
  // can have a NavGroup with id `contracts` AND a child rendering the
  // same destination without TabId collision.
  contractsAll: '/contracts',
  contractsCreate: '/contracts/create',
  contractsPendingSignatures: '/contracts/pending-signatures',
  projects: '/projects',
  // Lead routing is configured from inside the Leads page (Phase 22) —
  // nest the URL to match the access pattern. Legacy `/automation/routing`
  // still redirects via the route layer.
  routing: '/leads/routing',
  renewals: '/renewals',

  // Workspace
  integrations: '/integrations',
  customObjects: '/custom-objects',
  automations: '/automations',
  invoices: '/invoices',
  finance: '/finance',
  // Synonym → /finance (see TabId comment). Same destination as `finance`.
  financeAll: '/finance',
  reports: '/reports',
  reportsBuilder: '/reports/builder',
  staff: '/staff',

  // Settings sub-tree — every settings child is `/settings/*` because
  // every enterprise SaaS treats settings as a hierarchical sub-app.
  settings: '/settings',
  /// Phase 9 — Settings/Admin sub-module (Company branding, Defaults,
  /// Email, Roles & Permissions tabs).
  settingsAdmin: '/settings/admin',
  profile: '/settings/profile',
  security: '/settings/security',
  roles: '/settings/roles',
  profiles: '/settings/profiles',
  permissions: '/settings/permissions',
  emailSenders: '/settings/email-senders',
  audit: '/settings/audit',
  billing: '/settings/billing',
  referrals: '/settings/referrals',
  developers: '/settings/developers',

  // Help
  faq: '/help',
}

/**
 * My Work hub tabs ↔ their own URLs.
 *
 * Tasks / Calendar / Booking links were `?tab=` values on `/activities`. They
 * are destinations people navigate to and bookmark, not view modes, so they
 * get real paths — which is also what makes `tabForPath` able to highlight
 * them. `today` is the hub's own landing tab and keeps the bare `/activities`.
 *
 * `suggestions` and `feed` stay nested under `/activities/*` because they only
 * exist inside the hub and have no sidebar entry of their own.
 */
export type ActivitiesTabId = 'today' | 'tasks' | 'calendar' | 'booking' | 'suggestions' | 'feed'

export const PATH_FOR_ACTIVITIES_TAB: Record<ActivitiesTabId, string> = {
  today: '/activities',
  tasks: '/tasks',
  calendar: '/calendar',
  booking: '/scheduling',
  suggestions: '/activities/suggestions',
  feed: '/activities/feed',
}

/** Reverse lookup: which My Work tab a pathname selects. Longest-prefix wins so
 *  `/activities/feed` beats the bare `/activities`. */
export function activitiesTabForPath(pathname: string): ActivitiesTabId {
  let best: { tab: ActivitiesTabId; len: number } | null = null
  for (const [tab, p] of Object.entries(PATH_FOR_ACTIVITIES_TAB) as Array<[ActivitiesTabId, string]>) {
    if (pathname === p || pathname.startsWith(p + '/')) {
      if (!best || p.length > best.len) best = { tab, len: p.length }
    }
  }
  return best?.tab ?? 'today'
}

/**
 * First path segments that belong to the authenticated app, and therefore
 * belong under the `/w/<slug>` workspace prefix.
 *
 * Derived from `PATH_FOR_TAB` so a new tab can never be forgotten here, plus
 * the legacy/redirect-only roots that no longer have a tab of their own. The
 * set exists because the router ALSO serves single-segment CMS pages from a
 * `:slug` catch-all — `/leads` must be recognised as an app route while
 * `/about-us` must be left alone as a CMS page. An explicit closed list is the
 * only way to tell those apart, and deriving most of it keeps the manual part
 * small.
 */
export const APP_ROOT_SEGMENTS: ReadonlySet<string> = new Set([
  ...Object.values(PATH_FOR_TAB)
    .map((p) => p.split('?')[0]!.split('/')[1])
    .filter((seg): seg is string => Boolean(seg)),
  // Redirect-only roots kept alive for bookmarks (see router.tsx). They have no
  // PATH_FOR_TAB entry because nothing navigates TO them any more.
  'tasks',
  'calendar',
  'scheduling',
  'email-engagement',
  'duplicates',
  'conversations',
  'contacts',
  'inbox',
  'auto-responses',
  'dashboards',
  'automation',
  'segments',
  'forms',
])

/**
 * Tabs that have a detail path of the form `/<list>/:id`. The shell
 * routes these to the corresponding detail panel. Anything not in this
 * set keeps detail state internal to its panel (e.g. Accounts uses a
 * master-detail layout inside one route).
 */
export const TABS_WITH_DETAIL: ReadonlySet<TabId> = new Set([
  'leads',
  'pipeline',
  'integrations',
  'invoices',
  'contracts',
  'projects',
])

/**
 * Implicit parent for sub-pages that don't appear in the sidebar but
 * should still surface a parent crumb. Phase 21: `autoResponses` is
 * reached via a button on the Chat header rather than a sidebar
 * entry, so we tag it as a logical child of Chat so the breadcrumb
 * reads "Vendor › Chat › Auto-responses" instead of "Vendor › ".
 *
 * If the tab also lives inside a `NavGroup`, that grouping wins —
 * this map only matters for tabs that are *otherwise orphaned*.
 */
export const IMPLICIT_PARENT_TAB: Partial<Record<TabId, TabId>> = {
  autoResponses: 'chat',
  routing: 'leads',
  reportsBuilder: 'reports',
}

/**
 * Words that already mean something as the segment after a record id
 * (`/contracts/<id>/edit`, `/projects/<id>/edit`). A display slug must never
 * collide with one, or a record named "Edit" would silently deep-link into the
 * editor. Cheaper to reserve them than to reason about route ranking.
 */
const RESERVED_SLUGS: ReadonlySet<string> = new Set([
  'edit',
  'deliverables',
  'payments',
  'amendments',
  'new',
  'create',
  'templates',
  'drafts',
  'sent',
  'archive',
  'settings',
  'builder',
  'routing',
  'duplicates',
  'kpis',
  'portfolio',
  'pending-signatures',
  'lists-admin',
])

/**
 * Human-readable trailing segment for a record URL — the part that makes
 * `/leads/lead_7Kq2mXf9Lp/northwind-trading` scannable instead of opaque.
 *
 * PURELY COSMETIC. Nothing reads it: the record is resolved from the id alone,
 * so a stale slug on an old link (the account got renamed) still opens the
 * right record. This is how Notion, Linear and Attio all treat it.
 *
 * Returns '' when there is nothing useful to say, and the caller then omits the
 * segment entirely rather than emitting a trailing slash or "untitled".
 */
export function recordSlug(displayName: string | null | undefined): string {
  if (!displayName) return ''
  const slug = displayName
    .toLowerCase()
    .normalize('NFKD')
    // Drop accents so "Café Ltd" → "cafe-ltd" rather than percent-encoding.
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48)
    // A trailing hyphen can reappear after the slice.
    .replace(/-+$/, '')
  if (!slug || RESERVED_SLUGS.has(slug)) return ''
  return slug
}

/**
 * Compute the URL for a tab id, optionally with a record id and a display name.
 * Throws on unknown tab id — fail fast in dev rather than silently
 * navigating to "/undefined".
 *
 * `recordId` should be the record's short `publicId` (`lead_7Kq2mXf9Lp`) where
 * the caller has one; a uuid still works and still resolves, so a caller that
 * only holds an id is correct, just less pretty. `displayName` appends the
 * cosmetic slug — omit it and the URL is simply `/leads/<id>`.
 */
export function pathForTab(
  tab: TabId,
  recordId?: string | null,
  displayName?: string | null,
): string {
  const base = PATH_FOR_TAB[tab]
  if (!recordId) return base
  if (!TABS_WITH_DETAIL.has(tab)) return base
  const slug = recordSlug(displayName)
  return slug ? `${base}/${recordId}/${slug}` : `${base}/${recordId}`
}

/**
 * The id to put in a URL for a record: its short public id when the endpoint
 * returns one, else the uuid.
 *
 * One function rather than `record.publicId || record.id` at each call site,
 * because the fallback IS the compatibility guarantee — a surface whose
 * endpoint does not return `publicId` yet must degrade to the old working URL,
 * never to `/leads/undefined`. Stated once, it cannot be got wrong in the
 * fifth place.
 */
export function publicIdOf(record: { id: string; publicId?: string | null }): string {
  return record.publicId || record.id
}

/**
 * Same as `pathForTab`, for a record object straight off an API response.
 */
export function pathForRecord(
  tab: TabId,
  record: { id: string; publicId?: string | null } | null | undefined,
  displayName?: string | null,
): string {
  if (!record) return PATH_FOR_TAB[tab]
  return pathForTab(tab, publicIdOf(record), displayName)
}

/**
 * Reverse map: given a current pathname, identify which tab is active.
 * Returns null if the path doesn't correspond to any tab (e.g. legacy
 * `/vendor` before the redirect fires). Used by the sidebar / breadcrumb
 * to highlight the active section.
 *
 * Resolves longest-prefix-wins so `/settings/security` matches `security`
 * not `settings`. Required because `/settings` is a prefix of every
 * settings child path.
 */
export function tabForPath(pathname: string): TabId | null {
  let best: { tab: TabId; len: number } | null = null
  for (const [tab, p] of Object.entries(PATH_FOR_TAB) as Array<[TabId, string]>) {
    if (pathname === p || pathname.startsWith(p + '/')) {
      if (!best || p.length > best.len) {
        best = { tab, len: p.length }
      }
    }
  }
  return best?.tab ?? null
}

/**
 * Contract Module — typed helpers for the parameterised sub-paths.
 * Kept here so the path strings live next to the rest of the URL space
 * (single source of truth — see file header).
 */
/**
 * Deep-link to the Leads list pre-filtered to one stage column. The Leads
 * page reads `?stage=` into its initial filter (LeadsPanel). Used by the
 * AI CRM "Leads at a glance" funnel so clicking a stage bar lands on that
 * column — zero backend, just a URL param.
 */
export const leadsPathWithStage = (stage: string) =>
  `/leads?stage=${encodeURIComponent(stage)}`

export const pathForContractDetail = (id: string) => `/contracts/${id}`
export const pathForContractEdit = (id: string) => `/contracts/${id}/edit`
export const pathForContractDeliverables = (id: string) =>
  `/contracts/${id}/deliverables`
export const pathForContractPayments = (id: string) =>
  `/contracts/${id}/payments`
export const pathForContractAmendments = (id: string) =>
  `/contracts/${id}/amendments`
